<!-- Inline per-scanner claims picker (IT-F2-106 Phase 3d). Plain
     [value] / (change) — NO ngModel, no reactive forms. Every
     mutation emits a fresh entry clone via _emit(). All <select>
     bindings use STRING values so the DOM's select.value property
     (also a string) matches under Angular's === diff — otherwise
     `"3326" !== 3326` re-sets .value on every CD tick and closes
     any open dropdown mid-selection. -->
<div class="text-sm" *ngIf="schema; else noSchema">
  <div *ngFor="let f of fields" class="mb-3">
    <label class="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
      {{ f.label }}
      <span *ngIf="f.required" class="text-red-400 ml-0.5">*</span>
    </label>

    <!-- int + enum → <select>. Selected state driven by [selected] on
         each option (see grouped-mode block for the rationale). -->
    <select *ngIf="f.type === 'int' && f.enum"
            (change)="onScalarChange(f, $any($event.target).value)"
            class="bg-gray-50 border border-gray-300 dark:bg-app-black-100 dark:border-[#434863] dark:text-white text-gray-900 rounded px-2 py-1 text-xs w-full">
      <option *ngFor="let o of f.enum" [value]="o.value" [selected]="scalarValueString(f) === (o.value + '')">{{ o.label }}</option>
    </select>
    <!-- int free-text -->
    <input *ngIf="f.type === 'int' && !f.enum" type="number"
           [value]="scalarValueString(f)"
           (change)="onScalarChange(f, $any($event.target).value)"
           class="bg-gray-50 border border-gray-300 dark:bg-app-black-100 dark:border-[#434863] dark:text-white text-gray-900 rounded px-2 py-1 text-xs w-full" />

    <!-- bool -->
    <label *ngIf="f.type === 'bool'" class="inline-flex items-center gap-2 text-xs text-gray-900 dark:text-white cursor-pointer">
      <input type="checkbox"
             [checked]="!!entry[f.key]"
             (change)="onScalarChange(f, $any($event.target).checked)" />
      <span>enabled</span>
    </label>

    <!-- stringbool -->
    <select *ngIf="f.type === 'stringbool'"
            (change)="onScalarChange(f, $any($event.target).value)"
            class="bg-gray-50 border border-gray-300 dark:bg-app-black-100 dark:border-[#434863] dark:text-white text-gray-900 rounded px-2 py-1 text-xs w-full">
      <ng-container *ngIf="f.enum && f.enum.length; else defaultTrueFalse">
        <option *ngFor="let o of f.enum" [value]="o.value" [selected]="scalarValueString(f) === (o.value + '')">{{ o.label }}</option>
      </ng-container>
      <ng-template #defaultTrueFalse>
        <option value="true" [selected]="scalarValueString(f) === 'true'">true</option>
        <option value="false" [selected]="scalarValueString(f) === 'false'">false</option>
      </ng-template>
    </select>

    <!-- string + enum → <select> -->
    <select *ngIf="f.type === 'string' && f.enum"
            (change)="onScalarChange(f, $any($event.target).value)"
            class="bg-gray-50 border border-gray-300 dark:bg-app-black-100 dark:border-[#434863] dark:text-white text-gray-900 rounded px-2 py-1 text-xs w-full">
      <option *ngFor="let o of f.enum" [value]="o.value" [selected]="scalarValueString(f) === (o.value + '')">{{ o.label }}</option>
    </select>
    <!-- string free-text -->
    <input *ngIf="f.type === 'string' && !f.enum" type="text"
           [value]="scalarValueString(f)"
           (change)="onScalarChange(f, $any($event.target).value)"
           class="bg-gray-50 border border-gray-300 dark:bg-app-black-100 dark:border-[#434863] dark:text-white text-gray-900 rounded px-2 py-1 text-xs w-full" />

    <!-- Grouped-mode: "Subscanner - Mode" labels → one <select> per group.
         Selected state driven by [selected] on each option, NOT [value]
         on the select. Setting select.value on every CD tick was
         causing some browsers to dismiss the open native dropdown UI
         mid-interaction (mousedown opens → mousemove/mouseup fires CD
         → Angular re-sets .value → browser closes dropdown). [selected]
         on options mutates the option node's DOM property; browsers
         handle that without closing the dropdown. -->
    <div *ngIf="f.type === 'array' && f.enum && isGroupedArrayField(f)" class="flex flex-col gap-2">
      <div *ngFor="let g of arrayFieldGroups(f)" class="flex items-center gap-2">
        <span class="text-xs text-gray-700 dark:text-gray-200 font-medium min-w-[140px] flex-shrink-0">{{ g.group }}</span>
        <select
                (change)="onGroupSelectChange(f, g.options, $any($event.target).value)"
                class="bg-gray-50 border border-gray-300 dark:bg-app-black-100 dark:border-[#434863] dark:text-white text-gray-900 rounded px-2 py-1 text-xs flex-1">
          <option value="" [selected]="getGroupSelectedValue(f, g.options) === ''">Disabled</option>
          <option *ngFor="let o of g.options" [value]="o.value" [selected]="getGroupSelectedValue(f, g.options) === (o.value + '')">{{ o.modeLabel }}</option>
        </select>
      </div>
    </div>

    <!-- Array + enum flat checkbox grid -->
    <div *ngIf="f.type === 'array' && f.enum && !isGroupedArrayField(f)" class="flex flex-wrap gap-2">
      <label *ngFor="let o of f.enum"
             class="inline-flex items-center gap-1.5 text-xs text-gray-900 dark:text-white border border-gray-300 dark:border-[#434863] rounded px-2 py-0.5 cursor-pointer hover:border-app-teal-100">
        <input type="checkbox"
               [checked]="isArrayValueSelected(f, o.value)"
               (change)="toggleArrayValue(f, o.value)" />
        <span>{{ o.label }}</span>
      </label>
    </div>
    <!-- Array + options[] flat checkbox grid (legacy allowlist) -->
    <div *ngIf="f.type === 'array' && f.options && !f.enum" class="flex flex-wrap gap-2">
      <label *ngFor="let opt of f.options"
             class="inline-flex items-center gap-1.5 text-xs text-gray-900 dark:text-white border border-gray-300 dark:border-[#434863] rounded px-2 py-0.5 cursor-pointer hover:border-app-teal-100">
        <input type="checkbox"
               [checked]="isArrayValueSelected(f, opt)"
               (change)="toggleArrayValue(f, opt)" />
        <span>{{ opt }}</span>
      </label>
    </div>

    <p *ngIf="f.description" class="mt-1 text-[10px] text-gray-500 dark:text-gray-400">
      {{ f.description }}
    </p>
  </div>
</div>

<ng-template #noSchema>
  <p class="text-[11px] text-gray-500 dark:text-gray-400">
    No claims schema on this scanner or its customer.
  </p>
</ng-template>
